/**
 * CAMP AND GO SLOW custom asset — Cable Cherry pack builder + pack cart lock.
 *
 * Pairs with blocks/custom-cherry-pack.liquid (the builder form) and the
 * marked customizations in snippets/cart-products.liquid (locked quantity,
 * remove-whole-pack button).
 *
 * Packs are sold as fixed sets only: the builder adds N variant quantities in
 * one /cart/add.js call, tagging every line with a hidden `_Pack` property
 * (value = product handle). In the cart those lines can't be quantity-edited,
 * and removing any of them removes every line with the same `_Pack` value —
 * the mix can only be changed by rebuilding the pack on the product page.
 *
 * Standalone vanilla module on purpose: no @theme/* imports. Cart UI refresh
 * relies on dispatching a bubbling `cart:update` event with no sections
 * payload, which makes Horizon's cart components re-render themselves.
 *
 * Stock: each color is a tracked variant, so a slot can be filled with a color
 * that doesn't have enough left. Shopify would reject the whole add (the add
 * is all-or-nothing) and the customer would get a raw inventory error with no
 * hint of which slot to change. Instead the builder caps slots against the
 * per-color remaining stock stamped into `data-variant-limits`, minus whatever
 * is already in the cart, and relabels/disables options it can't honour. The
 * server-side check is still the authority — this only keeps the common case
 * from turning into an error message.
 */

const CART_UPDATE_EVENT = 'cart:update';

/** Dispatch a Horizon-compatible cart:update event so drawer/bubble/page refresh. */
function dispatchCartUpdate(target, resource, sourceId, data) {
  const event = new CustomEvent(CART_UPDATE_EVENT, { bubbles: true });
  // Horizon's event classes assign `detail` directly rather than using
  // CustomEvent's readonly init, so match that shape.
  Object.defineProperty(event, 'detail', {
    value: { resource, sourceId, data: { ...data } },
  });
  target.dispatchEvent(event);
}

class CgsCherryPackBuilder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form.cgs-cherry-pack');
    if (!this.form) return;

    this.errorEl = this.form.querySelector('[data-cgs-pack-error]');
    this.soldOutEl = this.form.querySelector('[data-cgs-pack-soldout]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.buyLabel = this.submitButton.textContent;
    this.busy = false;

    this.slots = Array.from(this.form.querySelectorAll('[data-cgs-cherry-slot]'));
    // Remaining stock per variant id. A missing entry means uncapped (untracked
    // variant, or one that oversells) — those are never blocked here.
    this.limits = new Map();
    try {
      const stamped = JSON.parse(this.form.dataset.variantLimits || '{}');
      for (const [id, remaining] of Object.entries(stamped)) {
        if (typeof remaining === 'number') this.limits.set(Number(id), Math.max(0, remaining));
      }
    } catch (error) {
      console.error(error);
    }

    // Tolerate markup rendered before data-label existed (asset and template
    // don't necessarily reach the CDN in the same instant).
    for (const slot of this.slots) {
      for (const option of slot.options) {
        if (option.value && !option.dataset.label) option.dataset.label = option.textContent.trim();
      }
      slot.addEventListener('change', () => this.#applyLimits());
    }

    this.#applyLimits();
    this.#subtractCartQuantities();

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#addPack();
    });
  }

  /**
   * Re-derive slot/option state from the current picks and remaining stock:
   * clear picks that no longer fit, disable+relabel options that are used up,
   * and flip the whole builder to sold out when no full pack is left.
   */
  #applyLimits() {
    const counts = new Map();
    for (const slot of this.slots) {
      const id = Number(slot.value);
      if (!id) continue;
      const used = counts.get(id) || 0;
      if (used >= this.#limitFor(id)) {
        slot.value = '';
        continue;
      }
      counts.set(id, used + 1);
    }

    for (const slot of this.slots) {
      const selected = Number(slot.value);
      for (const option of slot.options) {
        const id = Number(option.value);
        if (!id || !this.limits.has(id)) continue;
        const limit = this.limits.get(id);
        // A slot never blocks the color it is already showing.
        const usedElsewhere = (counts.get(id) || 0) - (id === selected ? 1 : 0);
        const blocked = usedElsewhere >= limit;
        option.disabled = blocked;
        if (!blocked) {
          option.textContent = option.dataset.label;
        } else {
          option.textContent = `${option.dataset.label} — ${limit > 0 ? `Only ${limit} left` : 'Sold out'}`;
        }
      }
    }

    const soldOut = this.#remainingCapacity() < this.slots.length;
    if (this.soldOutEl) this.soldOutEl.hidden = !soldOut;
    this.submitButton.disabled = soldOut || this.busy;
    this.submitButton.textContent = soldOut ? this.form.dataset.soldoutLabel || 'Sold out' : this.buyLabel;
  }

  #limitFor(id) {
    return this.limits.has(id) ? this.limits.get(id) : Infinity;
  }

  /** Total cherries still buyable across all colors; Infinity if any is uncapped. */
  #remainingCapacity() {
    const optionCount = this.slots[0] ? Array.from(this.slots[0].options).filter((o) => o.value).length : 0;
    if (this.limits.size < optionCount) return Infinity;
    let total = 0;
    for (const limit of this.limits.values()) total += limit;
    return total;
  }

  /**
   * Stock already sitting in this customer's cart is spoken for — Shopify
   * counts it, so the builder has to as well or a second pack fails at add.
   * Best-effort: a failed fetch just leaves the server check as the backstop.
   */
  async #subtractCartQuantities() {
    if (this.limits.size === 0) return;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      let changed = false;
      for (const item of cart.items || []) {
        if (!this.limits.has(item.variant_id)) continue;
        this.limits.set(item.variant_id, Math.max(0, this.limits.get(item.variant_id) - item.quantity));
        changed = true;
      }
      if (changed) this.#applyLimits();
    } catch (error) {
      console.error(error);
    }
  }

  async #addPack() {
    // Native `required` and the disabled button already cover this; belt and
    // braces, because a cleared-by-#applyLimits slot would otherwise post a
    // variant id of 0.
    if (this.submitButton.disabled || this.slots.some((slot) => !slot.value)) return;

    const { packId: packPrefix, productId, errorGeneric } = this.form.dataset;

    // One id PER ADD, not per product: `_Pack` is the cart's grouping key, so a
    // constant value (the handle) makes every pack in the cart the same pack —
    // removing one would remove them all, and same-color lines from different
    // packs would merge. Matches the configurator's `cfb-…` scheme.
    const packId = `${packPrefix}-${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;

    // Tally slot picks into variant quantities.
    const quantities = new Map();
    for (const select of this.slots) {
      const id = Number(select.value);
      quantities.set(id, (quantities.get(id) || 0) + 1);
    }

    const items = Array.from(quantities, ([id, quantity]) => ({
      id,
      quantity,
      properties: { _Pack: packId },
    }));

    this.#setError(null);
    this.busy = true;
    this.submitButton.disabled = true;

    try {
      // Theme.routes.cart_add_url already ends in .js (scripts.liquid appends it).
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        // Shopify's message for inventory shortfalls names the variant; show it as-is.
        this.#setError(result.description || result.message || errorGeneric);
        return;
      }

      // Those cherries are in the cart now, so they're off the table for the
      // next pack built on this same page view.
      for (const item of items) {
        if (this.limits.has(item.id)) {
          this.limits.set(item.id, Math.max(0, this.limits.get(item.id) - item.quantity));
        }
      }

      const packSize = Number(this.form.dataset.packSize) || items.reduce((sum, item) => sum + item.quantity, 0);
      dispatchCartUpdate(this, result, this.id || 'cgs-cherry-pack', {
        source: 'product-form-component',
        itemCount: packSize,
        productId,
      });
    } catch (error) {
      console.error(error);
      this.#setError(errorGeneric);
    } finally {
      this.busy = false;
      this.#applyLimits();
    }
  }

  #setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }
}

if (!customElements.get('cgs-cherry-pack-builder')) {
  customElements.define('cgs-cherry-pack-builder', CgsCherryPackBuilder);
}

/* ------------------------------------------------------------------ */
/* Remove-whole-pack buttons (rendered by cart-products.liquid)        */
/* ------------------------------------------------------------------ */

let removingPack = false;

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element && event.target.closest('[data-cgs-remove-pack]');
  if (!button || removingPack) return;

  event.preventDefault();
  removingPack = true;
  button.setAttribute('disabled', '');

  const packId = button.getAttribute('data-cgs-remove-pack');

  try {
    const cart = await (await fetch('/cart.js')).json();
    const updates = {};
    for (const item of cart.items) {
      if (item.properties && item.properties._Pack === packId) {
        updates[item.key] = 0;
      }
    }
    if (Object.keys(updates).length === 0) return;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    const result = await response.json();

    dispatchCartUpdate(button, result, 'cgs-cherry-pack-remove', {
      source: 'cgs-cherry-pack-remove',
      itemCount: result.item_count,
    });
  } catch (error) {
    console.error(error);
  } finally {
    removingPack = false;
    button.removeAttribute('disabled');
  }
});
